import { useState, useEffect } from 'react';
import {
  UserCalendarUpdate,
  UserCalendarCreateBody,
  CalendarListItem,
} from 'presentations/api/schema/user_calendar';
import { get, post } from 'infras/rest_client';
import { apiPath } from 'presentations/routes/path';
import {
  AccountCalendarListItem,
  AccountListItem,
} from 'presentations/api/schema/account';
import { useRequest } from 'presentations/views/hooks/request';
import { ErrorList } from 'domains/models';

interface Props {
  isOpen?: boolean;
}

export function useCalendarListDialog(props: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasChanged, setHasChanged] = useState<boolean>(false);
  const [accountCalendars, setAccountCalendars] = useState<
    AccountCalendarListItem[]
  >([]);
  const [selectedCalendars, setSelectedCalendars] = useState<
    UserCalendarUpdate[]
  >([]);
  const [calendarErrors, setCalendarErrors] = useState<ErrorList>({});

  useEffect(() => {
    if (!props.isOpen) return;
    fetch();
  }, [props.isOpen]);

  const accountsRequest = useRequest(
    props.isOpen ? apiPath('/v1/accounts?filter=calendar') : null,
  );
  const accounts: AccountListItem[] = accountsRequest.data || [];

  const fetch = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        new Promise(async (resolve, reject) => {
          const result = await get(apiPath('/v1/calendars?isReadable=1'));
          setCalendarErrors(result.errors);
          if (!result.isSuccess) {
            reject();
            return;
          }
          setAccountCalendars(result.data);
          const selected: UserCalendarUpdate[] = [];
          result.data.forEach((account: AccountCalendarListItem) => {
            account.calendars.some((calendar) => {
              if (!calendar) return;
              if (calendar.isIntegrated) {
                selected.push({
                  accountId: account.id,
                  calendarId: calendar.id!,
                  isWritable: calendar.isWritable,
                  isReadable: calendar.isReadable,
                  isSelected: calendar.isSelected,
                  color: calendar.color,
                  displayType: calendar.displayType,
                  visibilityType: calendar.visibilityType,
                });
              }
            });
          });
          setSelectedCalendars(selected);
          // setCalendarErrors(result.errors);
          resolve(true);
        }),
      ]);
    } catch (error) {
      console.debug(error);
    }
    setIsLoading(false);
  };

  const onCalendarSelect = async (
    accountId: number,
    calendar: CalendarListItem,
  ) => {
    const newSelected: UserCalendarUpdate[] = [...selectedCalendars];
    let isExist = false;
    for (let i = 0; i < selectedCalendars.length; i++) {
      const selectedCalendar: UserCalendarUpdate = selectedCalendars[i];
      if (
        selectedCalendar.accountId === accountId &&
        selectedCalendar.calendarId === calendar.id
      ) {
        isExist = true;
        newSelected.splice(i, 1);
        break;
      }
    }
    if (!isExist) {
      newSelected.push({
        accountId,
        calendarId: calendar.id!,
        isWritable: true,
        isReadable: true,
        isSelected: calendar.isSelected,
        color: calendar.color,
        displayType: calendar.displayType,
        visibilityType: calendar.visibilityType,
      });
    }
    setSelectedCalendars(newSelected);
    setHasChanged(true);
    try {
      const data: UserCalendarCreateBody = {
        calendars: newSelected,
      };
      const result = await post(apiPath('/v1/user_calendars/create'), {
        data,
      });
    } catch (error) {
      console.debug(error);
    }
  };

  return {
    state: {
      isLoading,
      hasChanged,
      accounts,
      accountCalendars,
      selectedCalendars,
      calendarErrors,
    },
    actions: {
      onCalendarSelect,
    },
  };
}
